'use client';

import { useMemo } from 'react';
import type { TeamData } from '../Cteamcard';
import {
  TEAM_LIST_CATEGORY,
  getTeamIdsForListCategory,
  useTeamsContext,
} from '@/contexts/teams';
import type { QueryStatus } from '@/types/async-protocol';
import type { TeamPreviewData } from './Chero.types';
import {
  buildTeamPreviewMap,
  selectHeroTeamsData,
} from './Chero.team-selectors';

interface UseHeroTeamsResult {
  teams: TeamPreviewData[];
  heroTeamsData: TeamData[];
  hasTeams: boolean;
  teamsLength: number;
  heroTeamsDataLength: number;
  teamsQueryStatus: QueryStatus;
}

/**
 * The hero carousel is the CATALOGUE, and only the catalogue.
 *
 * It reads `publicTeams` rather than the merged list on purpose: a viewer's own
 * saved teams are indistinguishable from catalogue teams once they are cards in
 * this carousel, and they are unbounded — a viewer with forty saved teams would
 * turn the landing page's first impression into their private shelf. Owned
 * teams get their own row in the "all teams" gallery, which is where the viewer
 * goes looking for them.
 *
 * It follows that this carousel renders identically signed in and signed out,
 * and never waits on the auth handshake or the owned query.
 */
export function useHeroTeams(): UseHeroTeamsResult {
  const { publicTeams, status } = useTeamsContext();

  const teamById = useMemo(
    () => buildTeamPreviewMap({ teams: publicTeams }),
    [publicTeams],
  );
  const teamIds = useMemo(
    () => getTeamIdsForListCategory(publicTeams, TEAM_LIST_CATEGORY.All),
    [publicTeams],
  );
  const heroTeamsData = useMemo(
    () => selectHeroTeamsData({ teamIds, teamById }),
    [teamById, teamIds],
  );

  return {
    teams: publicTeams,
    heroTeamsData,
    hasTeams: heroTeamsData.length > 0,
    teamsLength: publicTeams.length,
    heroTeamsDataLength: heroTeamsData.length,
    teamsQueryStatus: status,
  };
}
